import React, { useEffect, useState } from 'react';
import { Avatar, ThreadMetrics } from '@amzn/cuminuicomponents';
import { useRouter } from 'next/router';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  fetchSearchResults,
  SearchType,
  setInputFocused,
  setInputValue,
  setRefresh,
} from 'src/redux/slices/postSearch';
import { AppState } from 'src/redux/store';
import FetchStatus from 'src/types/FetchStatus';
import { KatButton, KatIcon, KatLink } from '@amzn/katal-react';
import Link from 'next/link';
import {
  getGroupListing,
  getThreadDetail,
  getUserProfileDetail,
  THREAD_DETAIL_CREATE,
  THREAD_LISTING,
} from 'src/constants/pages';
import {
  deserializeFromHtml, serializeToPlaintext, useMetrics, useTranslation,
} from 'src/utils';
import { Post, SearchDataRequestType } from 'src/types/Search';
import { PostType } from 'src/types/DiscussionPost';
import { redirectToLoginPage } from 'src/utils/request';
import defaultAvatars from 'src/constants/defaultAvatars';
import useValidateUserAction from 'src/redux/hooks/useValidateUserAction';
import subMonths from 'date-fns/subMonths';
import { ButtonTypes, MetricKeys } from 'src/constants/metrics';
import Typeahead from './Typeahead';
import { createSearchAPIContract } from './search-listing/utils/createSearchAPIContract';

const SearchBar = () => {
  const { push, pathname } = useRouter();
  const { t } = useTranslation();
  const {
    username,
    userId,
    avatarSrc = '/communities/images/placeholder/default_avatar_1.svg',
  } = useAppSelector(({ userInfo }: AppState) => userInfo);
  const inputValue = useAppSelector(
    ({ postSearch }: AppState) => postSearch[SearchType.searchBar].inputValue,
  ) as string;
  const showThreadListingInputMinLength = useAppSelector(
    ({ postSearch }) => postSearch.threadListing.showInputMinLength,
  );
  const {
    status,
    posts: searchResults,
  } = useAppSelector(({ postSearch }: AppState) => postSearch[SearchType.searchBar]);
  const dispatch = useAppDispatch();
  const [showSearchResults, setShowSearchResults] = useState(true);
  const { validateUserAction } = useValidateUserAction();
  const { clickMetric } = useMetrics();

  useEffect(() => {
    if ([THREAD_LISTING, getGroupListing('[groupId]')].includes(pathname)) {
      setShowSearchResults(false);
    } else if (!showSearchResults) {
      setShowSearchResults(true);
    }
  }, [pathname]);

  const getAvatar = () => {
    if (username) {
      return (
        <div className="rounded py-1.5 hover:bg-kat-zircon ltr:pr-3.5 ltr:pl-2.5 rtl:pr-2.5 rtl:pl-3.5">
          <Link
            prefetch={false}
            data-testid="user-profile-link"
            className="text-kat-nordic no-underline"
            onClick={() => clickMetric({ metricName: ButtonTypes.myProfile })}
            href={getUserProfileDetail(userId!)}
          >
            <Avatar
              src={avatarSrc}
              text={username}
            />
          </Link>
        </div>
      );
    }
    return (
      <div className="flex flex-row items-center gap-x-3">
        <Avatar
          src={defaultAvatars[0]}
        />
        <KatLink
          data-testid="signin-link"
          onClick={() => {
            clickMetric({ metricName: ButtonTypes.signIn });
            redirectToLoginPage();
          }}
          className="cursor-pointer text-sm"
          target="_self"
        >
          {t('searchBar_signIn')}
        </KatLink>
      </div>
    );
  };

  const onFocusHandler = () => dispatch(setInputFocused({
    type: SearchType.searchBar,
    focused: true,
  }));

  const onBlurHandler = () => dispatch(setInputFocused({
    type: SearchType.searchBar,
    focused: false,
  }));

  return (
    <div className="border border-solid border-kat-athens border-x-transparent bg-kat-snow py-1.5">
      <div className="kat-row container items-center">
        <div className="kat-col-sm-3 flex ltr:pl-0 rtl:pr-0">
          {getAvatar()}
        </div>
        <div className="kat-col-sm-6">
          <Typeahead
            uniqueId="discussion-search"
            value={inputValue}
            onChange={(e) => {
              dispatch(setInputValue({
                type: SearchType.searchBar,
                inputValue: e.currentTarget.value.trim(),
              }));
            }}
            onFocusAction={onFocusHandler}
            onBlurAction={onBlurHandler}
            delay={300}
            onSearch={() => {
              if (showSearchResults) {
                dispatch(fetchSearchResults({
                  type: SearchType.searchBar,
                  request: createSearchAPIContract({
                    searchTerm: inputValue!.trim(),
                    pageSize: 5,
                    searchDataRequestType: SearchDataRequestType.SEARCH_DATA,
                    startDate: subMonths(new Date(), 12).getTime(),
                    endDate: new Date().getTime(),
                  }),
                }));
              }
            }}
            onSelect={(value, index) => {
              const { postType, threadId, postId } = value as Post;
              if (postType === PostType.op) {
                clickMetric({
                  metricName: ButtonTypes.searchBar,
                  metricValue: threadId,
                  countMetrics: [{ metricName: MetricKeys.postIndex, metricValue: index }],
                });
                push(getThreadDetail(threadId));
              } else {
                clickMetric({
                  metricName: ButtonTypes.searchBar,
                  metricValue: postId,
                  countMetrics: [{ metricName: MetricKeys.postIndex, metricValue: index }],
                });
                push({
                  pathname: getThreadDetail(threadId),
                  query: { postId },
                });
              }
            }}
            onPressEnter={() => {
              if (pathname !== THREAD_LISTING && pathname !== getGroupListing('[groupId]')) {
                push(THREAD_LISTING);
              } else if (!showThreadListingInputMinLength) {
                dispatch(setRefresh({ refresh: true, type: SearchType.threadListing }));
              }
            }}
            noOption={<div className="select-none p-4">{t('noDiscussions')}</div>}
            isLoading={status === FetchStatus.loading && inputValue.trim().length > 0}
            open={showSearchResults}
            placeholder={t('searchForums')}
            options={searchResults.map(({
              postType, threadId, postId, title, lastActivityTime,
              totalReplies, totalViews, content, contentMetadata,
            }) => ({
              key: postId,
              value: { postType, threadId, postId },
              label: (
                <div
                  data-testid="typeahead-option"
                  className="px-4 first:mt-2 last:mb-2 hover:outline hover:outline-2 hover:outline-kat-eastern"
                >
                  <div className="text-kat-eastern">{title}</div>
                  <div className="text-xs line-clamp-1">
                    {postType !== PostType.op && <KatIcon name="reply" size="tiny" className="text-kat-tarpon ltr:mr-1 rtl:ml-1" />}
                    {serializeToPlaintext(deserializeFromHtml({
                      html: content,
                      metadata: contentMetadata,
                    }))}
                  </div>
                  <div className="flex text-xs">
                    <ThreadMetrics
                      lastActivity={lastActivityTime}
                      replyCount={totalReplies || 0}
                      viewCount={totalViews || 0}
                    />
                  </div>
                </div>
              ),
            }))}
          />
        </div>
        <div className="kat-col-sm-3 ltr:pr-0 rtl:pl-0">
          <div className="flex justify-end">
            <Link prefetch={false} href={THREAD_DETAIL_CREATE}>
              <KatButton
                data-testid="start-discussion-button"
                label={t('searchBar_startDiscussion')}
                icon={<KatIcon name="edit" size="small" />}
                onClick={() => {
                  clickMetric({ metricName: ButtonTypes.createDiscussion });
                  validateUserAction();
                }}
              />
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SearchBar;
